import React, { useCallback } from 'react';
import {
  ButtonNewRecord,
  FieldDatepicker,
  FieldInput,
  FormCol,
  FormCol3,
  FormRow,
  promiseModalDialog,
  toastSuccess,
  toastWarning,
} from 'motion-components';
import { useFormikContext } from 'formik';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { isActiveField } from 'shared-pages/common/form-helpfunc';
import { useApolloClient, useMutation } from '@apollo/client';
import gql from 'graphql-tag';

import ITranExtraTable from './iTranLines-table';
import {
  FieldSelectDriver,
  FieldSelectSeries,
  FieldSelectVehicle,
  FieldSelectWasteOriginType,
  FieldSelectWasteReceptionPoint,
  FormFieldCollectionPointDlg,
  promiseModalSelectMultiIdocs,
} from 'shared-pages/components';
import { IdocWasteOriginTypes, NumberingSeries } from 'shared-pages';
import { TransIdocsTable } from './idocs-table';
import { isCombatt, isFotokiklosi, isMedicycle, isRebatteryLiion } from 'shared-pages/common/constants';
import { gqlUpdate as gqlUpdateIdoc } from '../../idocs/graphql';

const gqlGetIdocsByIds = gql`
  query getIdocs($where: String) {
    getIdocs(where: $where) {
      data {
        id
        identificationDocument
        materialId
        iTransLineId
        transferredWithITransId
      }
    }
  }
`;

export const ItransFormFields = ({ isNew, updateRecord, refetch, data }: any) => {
  const frmkCntx = useFormikContext();
  const { values, dirty } = frmkCntx as {
    values: {
      wasteOriginTypeId: number;
      fromWastereceptionpointId: number;
      toWastereceptionpointId: number;
      internalTransactionLines: any[];
    };
    dirty: boolean;
  };
  const { authStore, appStore } = useRootStore();
  const client = useApolloClient();
  const [updateIdoc] = useMutation(gqlUpdateIdoc);

  const { handleGraphqlError } = useHandleError('PiBaseFormFields');
  const { t } = useTranslation();

  function calcUserCanEdit() {
    const canEdit = appStore.isPlatformOwner || isNew;
    const canEditReasoning = '';
    return { canEdit, canEditReasoning };
  }
  const { canEdit, canEditReasoning } = calcUserCanEdit();
  const canEditSeriesSection = !isNew
    ? false
    : appStore.isPlatformOwner ||
      appStore.isCollector ||
      appStore.isCollectorDriver ||
      (appStore.isWasteReceptionPoint && (isNew || data?.wasteOriginTypeId === 2));

  // Restrict WasteReception Points to Collector's Only
  const _restrictWastereceptionpoints =
    (appStore.isCollector || appStore.isCollectorDriver) && (dirty || isNew) ? true : false;
  const _collectorId =
    appStore.isCollector || appStore.isCollectorDriver ? appStore.associatedId : undefined;

  console.log('_restrictWastereceptionpoints', _restrictWastereceptionpoints);
  console.log('_collectorId', _collectorId);

  /** Επιλογή εντύπων αναγνώρισης και σύνδεσή τους με την εσωτερική διακίνηση */
  const addIdocsBtnClicked = useCallback(async () => {
    if (isNew || !data?.id) {
      toastWarning(t('Αποθηκεύστε πρώτα την εσωτερική διακίνηση πριν επιλέξετε έντυπα.'));
      return;
    }

    const materialIds = (values?.internalTransactionLines ?? [])
      .map((line) => line.materialId)
      .filter(Boolean);
    const materialNames = (values?.internalTransactionLines ?? [])
      .map((line) => line.material?.name)
      .filter(Boolean);

    const initFilters = {
      isActive: true,
      wastereceptionpointId:
        values?.wasteOriginTypeId === IdocWasteOriginTypes.SecondaryStorage
          ? values?.fromWastereceptionpointId
          : values?.toWastereceptionpointId,
      materialId: materialIds.join(','),
      materialName: materialNames.join(', '),
      collectorId: _collectorId,
    };

    const res = await promiseModalSelectMultiIdocs({
      initFilters,
      appStore,
      title: t('Επιλογή Εντύπων Αναγνώρισης'),
      collector: _collectorId ? { id: _collectorId } : undefined,
    });
    if (!res?.length) return;

    try {
      const selectedIds = res.map((item) => item.id);
      const fetchResult = await client.query({
        query: gqlGetIdocsByIds,
        variables: { where: JSON.stringify({ id: { $in: selectedIds } }) },
        fetchPolicy: 'network-only',
      });
      const idocs = fetchResult?.data?.getIdocs?.data ?? [];

      for (const idoc of idocs) {
        // Έντυπο που δημιουργήθηκε από γραμμή (π.χ. επικίνδυνο απόβλητο) κρατά το iTransLineId
        const isLineGeneratedIdoc = idoc.iTransLineId && !idoc.transferredWithITransId;

        await updateIdoc({
          variables: {
            data: {
              id: idoc.id,
              transferredWithITransId: data.id,
              // Έντυπα ιχνηλασιμότητας: μόνο transferredWithITransId, όχι σύνδεση με γραμμή
              ...(isLineGeneratedIdoc ? {} : { iTransLineId: null }),
              idocExtras: [],
            },
          },
        });
      }
      toastSuccess(t('Επιτυχής σύνδεση εντύπων με την εσωτερική διακίνηση'));
      refetch?.();
    } catch (error) {
      handleGraphqlError('mutation-update-idoc', error);
    }
  }, [
    isNew,
    data?.id,
    values?.internalTransactionLines,
    values?.wasteOriginTypeId,
    values?.fromWastereceptionpointId,
    values?.toWastereceptionpointId,
    _collectorId,
    appStore,
    t,
    client,
    updateIdoc,
    refetch,
    handleGraphqlError,
  ]);

  const showIdocsSection = (isMedicycle || isRebatteryLiion || isCombatt) && data && !isNew;

  /** Αφαίρεση σύνδεσης εντύπου από την προέλευση αποβλήτων (όχι διαγραφή εντύπου) */
  const unlinkIdocBtnClicked = useCallback(
    async (idoc: { id: number; identificationDocument: string }) => {
      const answer = await promiseModalDialog({
        title: t('Αφαίρεση σύνδεσης'),
        text: `<div>Θέλετε να αφαιρέσετε το έντυπο <strong>${idoc.identificationDocument}</strong> από την προέλευση αποβλήτων;</div>`,
      });
      if (!answer) return;

      try {
        await updateIdoc({
          variables: {
            data: {
              id: idoc.id,
              transferredWithITransId: null,
              idocExtras: [],
            },
          },
        });
        toastSuccess(t('Αφαιρέθηκε η σύνδεση με το έντυπο {{code}}', { code: idoc.identificationDocument }));
        refetch?.();
      } catch (error) {
        handleGraphqlError('mutation-unlink-idoc', error);
      }
    },
    [t, updateIdoc, refetch, handleGraphqlError],
  );

  /**
   * Render
   */
  return (
    <div key={data?.id}>
      {/* 1st Row */}
      <FormRow>
        <FieldSelectSeries
          as={FormCol}
          required={true}
          label={t('Σειρά Αρίθμησης Εντύπου')}
          name="seriesId"
          seriestypeId={NumberingSeries.ITrans}
          disabled={!isNew}
        />
        <FormCol>
          <FieldInput
            disabled={!canEditSeriesSection}
            label={t('Αριθμός Εντύπου')}
            name="codeNumber"
            type="number"
            placeholder={t('Αφήστε κενό για Αυτόματη Αρίθμηση')}
          />
        </FormCol>
        <FieldInput
          disabled={!canEditSeriesSection}
          as={FormCol}
          label={t('Κωδικός Εντύπου')}
          name="code"
        />
        <FormCol className="col-md">{!isNew && isActiveField(t, canEdit)}</FormCol>
      </FormRow>
      {/* 2nd Row */}
      <FormRow>
        <FormCol className="col-md">
          <FieldDatepicker
            label={t('Ημερομηνία Διακίνησης')}
            name="transactionDate"
            disabled={!canEdit}
            required={true}
          />
        </FormCol>
        <FieldSelectWasteOriginType
          required={true}
          disabled={!canEdit}
          as={FormCol}
          name="wasteOriginTypeId"
          defaultValues={data}
          // onChange={onWasteOriginTypeIdChange}
        />
        {/* Λήψη απο Σημείο Συλλογής  */}
        {values?.wasteOriginTypeId === IdocWasteOriginTypes.PrimaryStorage && (
          <FormFieldCollectionPointDlg
            as={FormCol3}
            label={t('Σημείο Συλλογής')}
            // key={collpointKey}
            disabled={!canEdit}
            // onChange={onCollectionPointChange}
            idFieldName="fromCollectionpointId"
            nameFieldName="fromCollectionpoint.name"
            required={values?.wasteOriginTypeId === IdocWasteOriginTypes.PrimaryStorage}
          />
        )}
        {/* Λήψη απο Προσωρινή Αποθήκευση */}
        {values?.wasteOriginTypeId === IdocWasteOriginTypes.SecondaryStorage && (
          <FieldSelectWasteReceptionPoint
            as={FormCol}
            label={t('Προέλευση')}
            name="fromWastereceptionpointId"
            disabled={!canEdit}
            collectorId={_restrictWastereceptionpoints ? _collectorId : undefined}
            required={values?.wasteOriginTypeId === IdocWasteOriginTypes.SecondaryStorage}
          />
        )}
        {/* Εναπόθεση σε Προσωρινή Αποθήκευση */}
        <FieldSelectWasteReceptionPoint
          as={FormCol}
          label={t('Προορισμός')}
          name="toWastereceptionpointId"
          disabled={!canEdit}
          required={true}
        />
        {/* <FieldSelectCollector
          as={FormCol}
          label={t('Συλλέκτης')}
          name="collectorId"
          disabled={!canEdit}
        /> */}
      </FormRow>
      <FormRow>
        <FieldInput
          as={FormCol}
          label={t('Σημειώσεις')}
          name="notes"
          placeholder=""
          disabled={!canEdit}
        />

        <FieldSelectVehicle
          required={false}
          disabled={!canEdit}
          as={FormCol}
          collectorId={_collectorId ?? undefined}
          name="vehicleId"
          defaultValues={data}
          label="Αρ. Κυκλ. Οχήματος"
        />

        <FieldSelectDriver
          as={FormCol}
          required={false}
          disabled={!canEdit}
          collectorId={_collectorId ?? undefined}
          name="driverId"
          defaultValues={data}
        />

        <FormCol />
      </FormRow>

      {/* ************************************************************** */}
      {/* Απόβλητα Διακίνησης */}
      <div className="mb-5">
        <ITranExtraTable internalTransactionLines={values?.internalTransactionLines ?? []} />
      </div>
      {/* {frmkCntx && frmkCntx.errors && <div>{JSON.stringify(frmkCntx.errors)}</div>} */}

      {/* ************************************************************** */}
      {/* Συνδεδεμένα Έντυπα Αναγνώρισης */}
      {showIdocsSection && (
        <TransIdocsTable
          data={data?.idocs ?? []}
          canEdit={canEdit}
          onUnlinkIdoc={unlinkIdocBtnClicked}
          headerAction={
            <ButtonNewRecord
              className="align-self-center me-3"
              label={t('Έντυπα Αναγνώρισης')}
              onClick={addIdocsBtnClicked}
              disabled={!canEdit}
            />
          }
        />
      )}
    </div>
  );
};
